import { useCallback, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import useDebounce from "../../hooks/useDebounce";
import {
    clearRecent,
    clearRecentFromStorage,
    closePalette,
    hydrateRecent,
    loadRecentFromStorage,
    persistRecentToStorage,
    pushRecent,
    RecentSearch,
    searchFailure,
    searchStart,
    searchSuccess,
    selectSearch,
    setQuery,
} from "../../redux/search/search.slice";
import {
    searchApi,
    SearchEntityType,
    SearchResultItem,
    UnifiedSearchResults,
} from "../../services/apis/search.api";
import { getSearchableEntityTypes } from "./getSearchableEntityTypes";

export function useCommandPalette() {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const searchState = useAppSelector(selectSearch);
    const auth = useAppSelector((s) => s.auth) as any;
    const selectedOrganization = useAppSelector(
        (s) => s.organization.selectedOrganization
    );

    const allowedTypes = useMemo(
        () =>
            getSearchableEntityTypes({
                isAuthenticated: auth?.isAuthenticated,
                adminMode: auth?.adminMode,
                ownerMode: auth?.ownerMode,
                superAdminMode: auth?.superAdminMode,
                p3mSuperAdminMode: auth?.p3mSuperAdminMode,
                user: auth?.user,
            }),
        [auth]
    );

    const accessToken: string | undefined = auth?.access_token;
    const userId: string | undefined = auth?.user?._id;

    const debouncedQuery = useDebounce(searchState.query, 250);
    const abortRef = useRef<AbortController | null>(null);

    // Hydrate recent searches once when the palette first opens
    useEffect(() => {
        if (searchState.open && !searchState.recentHydrated) {
            dispatch(hydrateRecent(loadRecentFromStorage(userId)));
        }
    }, [searchState.open, searchState.recentHydrated, userId, dispatch]);

    // Fire debounced search
    useEffect(() => {
        const q = (debouncedQuery || "").trim();
        if (!searchState.open) return;
        if (q.length < 2) return;
        if (!selectedOrganization?._id) return;

        // Cancel any prior request
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        dispatch(searchStart());

        const header = accessToken
            ? {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${accessToken}`,
              }
            : undefined;

        searchApi
            .unifiedSearch(
                {
                    q,
                    organizationId: selectedOrganization._id,
                    types: allowedTypes,
                    limit: 5,
                },
                header,
                controller.signal
            )
            .then((resp) => {
                if (controller.signal.aborted) return;
                if (resp) {
                    dispatch(
                        searchSuccess({
                            results: resp.results,
                            totals: resp.totals,
                        })
                    );
                }
            })
            .catch((err) => {
                if (controller.signal.aborted) return;
                dispatch(searchFailure(err?.message || "Search failed"));
            });

        return () => controller.abort();
    }, [
        debouncedQuery,
        searchState.open,
        selectedOrganization?._id,
        allowedTypes,
        accessToken,
        dispatch,
    ]);

    const close = useCallback(() => {
        abortRef.current?.abort();
        dispatch(closePalette());
    }, [dispatch]);

    const setQueryValue = useCallback(
        (v: string) => dispatch(setQuery(v)),
        [dispatch]
    );

    // Filter results to only allowed types (defense-in-depth; backend already does this)
    const filteredResults: UnifiedSearchResults | null = useMemo(() => {
        if (!searchState.results) return null;
        const out = { ...searchState.results } as UnifiedSearchResults;
        (Object.keys(out) as SearchEntityType[]).forEach((t) => {
            if (!allowedTypes.includes(t)) out[t] = [];
        });
        return out;
    }, [searchState.results, allowedTypes]);

    const selectResult = useCallback(
        (item: SearchResultItem, type: SearchEntityType) => {
            const recent: RecentSearch = {
                q: searchState.query,
                at: Date.now(),
                url: item.url,
                title: item.title,
                type,
                openTaskId: item.openTaskId,
            };
            dispatch(pushRecent(recent));
            const next = [
                recent,
                ...searchState.recent.filter((r) => r.url !== recent.url),
            ].slice(0, 5);
            persistRecentToStorage(userId, next);

            // Navigate - for tasks use location.state so TaskAndGoals opens the detail panel
            if (type === "tasks" && item.openTaskId) {
                navigate(item.url, { state: { openTaskId: item.openTaskId } });
            } else {
                navigate(item.url);
            }
            close();
        },
        [navigate, searchState.query, searchState.recent, userId, dispatch, close]
    );

    const clearRecentSearches = useCallback(() => {
        dispatch(clearRecent());
        clearRecentFromStorage(userId);
    }, [dispatch, userId]);

    const replayRecent = useCallback(
        (r: RecentSearch) => {
            if (r.type === "tasks" && r.openTaskId) {
                navigate(r.url, { state: { openTaskId: r.openTaskId } });
            } else {
                navigate(r.url);
            }
            close();
        },
        [navigate, close]
    );

    return {
        open: searchState.open,
        query: searchState.query,
        loading: searchState.loading,
        results: filteredResults,
        totals: searchState.totals,
        error: searchState.error,
        recent: searchState.recent,
        allowedTypes,
        setQuery: setQueryValue,
        close,
        selectResult,
        replayRecent,
        clearRecentSearches,
    };
}
